import React, {
  useEffect, useState,
} from 'react';

import {
  Skeleton,
  Stack,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useResizeDetector } from 'react-resize-detector';

import { Lesson } from '../../services/classes/types';
import addDays from '../../utils/common/addDays';
import useVisibleElementsCount from '../../utils/react/hooks/useVisibleElementsCount';
import LessonChip from '../LessonChip';

const groupLessonsByWeek = (
  lessons: Lesson[], startDatetime: dayjs.Dayjs, numWeeks: number) => {
  const grouped: { [key: number]: Lesson[] } = {};

  lessons.forEach((lesson) => {
    const lessonDate = dayjs(lesson.startDatetime);

    if (lessonDate.isSameOrAfter(startDatetime)
    && lessonDate.isSameOrBefore(addDays(startDatetime, numWeeks * 7))) {
      const weekStart = lessonDate.startOf('week').valueOf();

      if (!grouped[weekStart]) {
        grouped[weekStart] = [];
      }

      grouped[weekStart].push(lesson);
    }
  });

  return grouped;
};

interface LessonScheduleViewProps {
  lessons?: Lesson[];
  startDatetime: number;
  regularDay?: number;
  minWeeks?: number;
  maxWeeks?: number;
}

const LessonScheduleView = ({
  lessons,
  startDatetime,
  minWeeks = 1,
  maxWeeks = 10,
  regularDay = 1,
}: LessonScheduleViewProps) => {
  const { width = 0, ref } = useResizeDetector();

  // 96px is the width of a single lesson chip and 8px is the spacing between them
  const visibleWeeks = useVisibleElementsCount(96 + 8, width, minWeeks, maxWeeks);

  const [groupedLessons, setGroupedLessons] = useState<{ [key: number]: Lesson[] }>({});

  const [weeksInRange, setWeeksInRange] = useState<number[]>([]);
  const [currentWeek, setCurrentWeek] = useState<Dayjs | undefined>(undefined);

  // Convert Sunday to 7
  const dayOfWeek = (regularDay || 7) - 1;

  useEffect(() => {
    setCurrentWeek(dayjs(startDatetime).startOf('week'));
  }, [startDatetime]);

  useEffect(() => {
    if (visibleWeeks && currentWeek) {
      const newWeeksInRange = [];
      let curr = currentWeek.valueOf();
      for (let i = 0; i < visibleWeeks; i += 1) {
        newWeeksInRange.push(curr);
        curr = addDays(dayjs(curr), 7).valueOf();
      }
      setWeeksInRange(newWeeksInRange);
    }
  }, [currentWeek, visibleWeeks]);

  useEffect(() => {
    if (lessons && visibleWeeks && currentWeek) {
      setGroupedLessons(groupLessonsByWeek(lessons, currentWeek, visibleWeeks));
    }
  }, [currentWeek, visibleWeeks, lessons]);

  if (!lessons) {
    // Render Skeleton visibleWeeks times
    return (
      <Stack
        direction="row"
        spacing={1}
      >
        {Array.from(Array(visibleWeeks).keys()).map((x) => (
          <Skeleton
            key={x}
            variant="rectangular"
            width={96}
            height={64}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      ref={ref as React.RefObject<HTMLDivElement>}
      direction="row"
      spacing={1}
    >
      {weeksInRange.slice(0, visibleWeeks).map((week) => {
        const isThisWeek = dayjs(week).isSame(dayjs(), 'week');
        return (
          <Stack key={week} direction="column" spacing={1}>
            {groupedLessons[week]?.map((lesson) => (
              <LessonChip
                key={lesson.id}
                lesson={lesson}
                isThisWeek={isThisWeek}
              />
            )) || (
              <LessonChip
                startDatetime={addDays(dayjs(week).startOf('week'), dayOfWeek).valueOf()}
                isThisWeek={isThisWeek}
              />
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
export default LessonScheduleView;
